import React, { useState, useEffect, useMemo, useCallback } from "react";
import TableCardv2 from "../../components/TableCardv2";
import BusinessIcon from "@mui/icons-material/Business";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Chip, Fab, Snackbar, Alert, Box, Button } from "@mui/material";
import { listarGruposEconomicos } from "../../services/api-empresas.service";
import Loading from "../../components/Loading";
import { debounce } from 'lodash';
import ModalCadastroGrupoEconomico from "../../components/CadastroEmpresas/ModalCadastroGrupoEconomico";
import {
  IfPermissao,
  usePermissoes,
  CHAVES_GRUPOS_ECONOMICOS_CRIAR,
  CHAVES_GRUPOS_ECONOMICOS_EDITAR,
  CHAVES_GRUPOS_ECONOMICOS_LEITURA,
} from "../../config/Context/PermissoesContext";

export default function GruposEconomicos() {
    const { pode } = usePermissoes();
    const [loading, setLoading] = useState(false);
    const [listaBruta, setListaBruta] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');

    // Estados para Modal
    const [modalOpen, setModalOpen] = useState(false);
    const [editingGroup, setEditingGroup] = useState(null);
    const [modalReadOnly, setModalReadOnly] = useState(false);

    // Estados para Snackbar
    const [snackbar, setSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('error');

    const showSnackbar = (message, severity = 'error') => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbar(true);
    };

    const handleNew = () => {
        setEditingGroup(null);
        setModalReadOnly(false);
        setModalOpen(true);
    };

    const handleEdit = (grupo) => {
        setEditingGroup(grupo);
        setModalReadOnly(false);
        setModalOpen(true);
    };

    const handleView = (grupo) => {
        setEditingGroup(grupo);
        setModalReadOnly(true);
        setModalOpen(true);
    };

    const handleSuccess = () => {
        showSnackbar('Operação realizada com sucesso!', 'success');
        loadGrupos();
    };

    const loadGrupos = useCallback(async () => {
        setLoading(true);
        try {
            const response = await listarGruposEconomicos();
            setListaBruta(response.gruposEconomicos || []);
        } catch (error) {
            console.error("Erro ao listar grupos", error);
            showSnackbar('Erro ao carregar grupos econômicos', 'error');
        } finally {
            setLoading(false);
        }
    }, []);

    const data = useMemo(() => {
        const podeVer = pode(CHAVES_GRUPOS_ECONOMICOS_LEITURA);
        const podeEditar = pode(CHAVES_GRUPOS_ECONOMICOS_EDITAR);
        return listaBruta.map((grupo) => {
            const btn = [];
            if (podeVer) {
                btn.push({
                    icon: <VisibilityIcon />,
                    title: "Visualizar",
                    onClick: () => handleView(grupo),
                });
            }
            if (podeEditar) {
                btn.push({
                    icon: <MoreHorizIcon />,
                    title: "Editar",
                    onClick: () => handleEdit(grupo),
                });
            }
            return {
                ...grupo,
                id: grupo.idGrupoEconomico,
                nome: grupo.nome,
                statusDisplay: (
                    <Chip
                        label={grupo.ativo ? "Ativo" : "Inativo"}
                        size="small"
                        sx={{
                            bgcolor: grupo.ativo ? '#e8f5e9' : '#ffebee',
                            color: grupo.ativo ? '#2e7d32' : '#c62828',
                            fontWeight: 'bold'
                        }}
                    />
                ),
                btn,
            };
        });
    }, [listaBruta, pode]);

    const handleSearch = (searchValue) => {
        setSearchTerm(searchValue);
        if (!searchValue || searchValue.trim() === '') {
            setFilteredData(data);
            return;
        }

        const searchLower = searchValue.toLowerCase();
        const filtered = data.filter(grupo =>
            grupo.nome?.toLowerCase().includes(searchLower) ||
            String(grupo.idGrupoEconomico).includes(searchLower)
        );
        setFilteredData(filtered);
    };

    const debouncedSearch = debounce(handleSearch, 1000);

    useEffect(() => {
        setFilteredData(data);
    }, [data]);

    useEffect(() => {
        loadGrupos();
    }, [loadGrupos]);

    const columns = [
        { key: "idGrupoEconomico", label: "ID" },
        { key: "nome", label: "Nome do Grupo" },
        { key: "statusDisplay", label: "Status" }
    ];

    const header = {
        icon: <BusinessIcon sx={{ fontSize: 30 }} />,
        title: "Grupos Econômicos"
    };

    const headerActions = (
        <IfPermissao anyOf={CHAVES_GRUPOS_ECONOMICOS_CRIAR} fallback={null}>
            <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleNew}
                sx={{
                    textTransform: "none",
                    bgcolor: "#0d47a1",
                    "&:hover": { bgcolor: "#1565c0" },
                }}
            >
                Novo grupo econômico
            </Button>
        </IfPermissao>
    );

    return (
        <div style={{ padding: '20px' }}>
            {loading ? (
                <Loading loading={loading} overlay={true} />
            ) : (
                <TableCardv2
                    header={header}
                    data={filteredData}
                    columns={columns}
                    loading={loading}
                    debounce={debouncedSearch}
                    searchPlaceholder="Buscar por Grupo Econômico"
                    complementaryHeaderBottom={
                        <Box sx={{ display: "flex", alignItems: "center", mr: 1 }}>{headerActions}</Box>
                    }
                />
            )}

            <IfPermissao anyOf={CHAVES_GRUPOS_ECONOMICOS_CRIAR} fallback={null}>
                <Fab
                    color="primary"
                    aria-label="Novo grupo econômico"
                    onClick={handleNew}
                    sx={{
                        position: 'fixed',
                        bottom: 24,
                        right: 24,
                        backgroundColor: '#0d47a1',
                        '&:hover': {
                            backgroundColor: '#1565c0',
                        },
                    }}
                >
                    <AddIcon />
                </Fab>
            </IfPermissao>

            <ModalCadastroGrupoEconomico
                open={modalOpen}
                onClose={() => {
                    setModalOpen(false);
                    setModalReadOnly(false);
                }}
                onSuccess={handleSuccess}
                initialData={editingGroup}
                readOnly={modalReadOnly}
            />

            <Snackbar
                open={snackbar}
                autoHideDuration={5000}
                onClose={() => setSnackbar(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Alert
                    onClose={() => setSnackbar(false)}
                    severity={snackbarSeverity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </div>
    );
}
